import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { getStores } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';

const AllStoresPage = () => {
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = stores.filter(store =>
        store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        store.domain.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredStores(filtered);
    } else {
      setFilteredStores(stores);
    }
  }, [searchQuery, stores]);

  const loadStores = async () => {
    setLoading(true);
    try {
      const allStores = await getStores();
      setStores(allStores);
      setFilteredStores(allStores);
    } catch (error) {
      console.error('Error loading stores:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Header />

      {/* Main Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            All <span className="text-slate-900">Stores</span>
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            Browse coupons from {stores.length} popular stores
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search stores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg border-2 border-slate-200 focus:border-slate-900 rounded-2xl shadow-lg"
              />
            </div>
          </div>

          {/* Stores Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading stores...</p>
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No stores found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStores.map(store => (
                <Link key={store.id} to={`/store/${store.id}`}>
                  <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-slate-300 h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-20 h-20 bg-white rounded-xl shadow-md flex items-center justify-center flex-shrink-0">
                          <img
                            src={store.logo}
                            alt={store.name}
                            className="w-16 h-16 object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name)}&background=1e293b&color=fff&size=128`;
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-bold text-slate-900 mb-2">{store.name}</h3>
                          <p className="text-sm text-slate-600 mb-3 line-clamp-2">{store.description}</p>
                          <div className="flex items-center space-x-2">
                            <Badge className="bg-slate-900 text-white">
                              {store.total_coupons} coupons
                            </Badge>
                            {store.featured && (
                              <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                                Featured
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AllStoresPage;
